// Parsing for the dashboard's one-click login links.
//
// Two shapes reach /magic, and both must keep working:
//
//   ?magic=<base64url(username:password)>&autoLogin=true   what the backend mints today
//   ?username=<email>&password=<pw>&autoLogin=true         every link already in an inbox
//
// The second shape has no expiry and the emails carrying it were sent months ago, so it is
// not a deprecation we can time out — it is permanent.

// Recovers the credential pair from a link's query params.
//
// Returns null for anything incomplete rather than a half-filled object, so the caller can
// show its not-found branch instead of handing `undefined` to the login form.
export function readCredentials(searchParams) {
  const magic = searchParams.get('magic');

  if (magic) {
    // The polyfill accepts both the base64 and base64url alphabets, so a link minted before
    // the switch to base64url still decodes here.
    const decoded = Buffer.from(magic, 'base64').toString('utf-8');
    // Split on the first colon only: an address cannot contain one, a password might.
    const separator = decoded.indexOf(':');
    if (separator < 1 || separator === decoded.length - 1) {
      return null;
    }
    // Byte-exact out of the blob, so unlike the legacy shape it needs no repair.
    return {
      username: decoded.slice(0, separator),
      password: decoded.slice(separator + 1)
    };
  }

  const username = searchParams.get('username');
  const password = searchParams.get('password');
  if (!username || !password) {
    return null;
  }

  // Legacy links interpolate the address into the URL without escaping it, so a '+' in it
  // arrives here as a space. Undo that: an address cannot contain a space, so nothing
  // legitimate is harmed. Applied only to this shape — the base64 one is already exact.
  return { username: username.replaceAll(' ', '+'), password };
}

export default readCredentials;
